import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Empty } from "antd";

import {
  SortButton,
  Buttons,
  Spacer,
  popover,
  boxContainer,
  inputFields,
  university,
  Loader,
  upload,
} from "../../components";

import { UniversityAction } from "../../store/actions";

import "./styles.css";

const UniversityLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const boxRef = useRef();

  const viewMode = location.pathname.split("/")[2];

  const {
    topPicks,
    trending,
    popularInMyCountry,
    topBusiness,
    topMedicine,
    topLaw,
    topEngineering,
    topArtsAndHumanities,
    topPsychology,
    youMayLike,
    isGridLoading,
    count,
  } = useSelector((state) => state.university);

  const [gridView, setGridView] = useState(viewMode === "grid");

  useEffect(() => {
    dispatch(UniversityAction.getUniversitiesGrid({}));
  }, [gridView]);

  const handleViewChange = (isGridView) => {
    setGridView(isGridView);
    navigate(isGridView ? "/universities/grid" : "/universities/list");
  };

  const renderButton = (isGridView, label) =>
    isGridView ? (
      <Buttons.DefaultButton
        size="small"
        onClick={() => handleViewChange(isGridView)}
        title={
          label === "List View"
            ? "Click to switch to a detailed, list-style view of universities."
            : "Click to switch to a visual, grid-style layout of universities."
        }
      >
        {label}
      </Buttons.DefaultButton>
    ) : (
      <Buttons.WhiteButton
        size="small"
        onClick={() => handleViewChange(isGridView)}
        title={
          label === "List View"
            ? "Click to switch to a detailed, list-style view of universities."
            : "Click to switch to a visual, grid-style layout of universities."
        }
      >
        {label}
      </Buttons.WhiteButton>
    );

  const filterButtons = [
    {
      title: "Countries",
      placement: "bottomLeft",
      hoverTitle: "Click to filter universities by country.",
    },
    {
      title: "Area of Study",
      hoverTitle:
        "Click to explore universities by fields such as Arts & Humanities, Engineering & Technology, and more.",
    },
    {
      title: "Budget",
      hoverTitle:
        "Click to filter universities based on your budget range in USD.",
    },
    {
      title: "Scholarships",
      hoverTitle:
        "Click to filter universities offering scholarships or those without available scholarships.",
    },
    {
      title: "University Tags",
      hoverTitle:
        "Click to filter universities by tags such as Group of 8, Russell Group, Universitas 21, and more.",
    },
  ];

  return (
    <div className="universities">
      <div className="flex justify-center gap-2">
        <inputFields.DefaultSearchBox
          onClick={() => navigate("/universities/list")}
        />
        <div className="w-[7.5em]">
          <Buttons.DefaultButton
            size="medium"
            width="full"
            style={{ height: "100%" }}
            onClick={() => navigate("/universities/list")}
          >
            Search
          </Buttons.DefaultButton>
        </div>
        <div className="w-[10.5em]">
          <Buttons.DefaultButton
            size="medium"
            width="full"
            style={{ height: "100%" }}
            onClick={() => navigate("/universities/add-university")}
          >
            Add University
          </Buttons.DefaultButton>
        </div>
        <upload.UploadUni />
      </div>
      <Spacer height={20} />
      <div className="flex gap-2">
        {filterButtons?.map((item, index) => (
          <Buttons.DefaultButton
            key={index}
            size="small"
            onClick={() => navigate("/universities/list")}
            title={item?.hoverTitle}
          >
            {item?.title}
          </Buttons.DefaultButton>
        ))}
      </div>
      <Spacer height={20} />
      <boxContainer.UniversityBoxContainer>
        <div className="flex justify-between border-b border-b-liteBoxBorder px-4 py-3">
          <div className="flex items-center gap-3 ">
            <div className="font-bold text-[1.25em] pl-2">
              {count.toLocaleString()} Universities
            </div>
            {renderButton(true, "Grid View")}
            {renderButton(false, "List View")}
          </div>
          {!gridView && (
            <popover.DefaultPopover content={columnFilter}>
              <SortButton />
            </popover.DefaultPopover>
          )}
        </div>

        <div className="p-4">
          <div className="header-sm pl-3">Trending Universities</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : trending?.length === 0 ? (
              <Empty />
            ) : (
              <div ref={boxRef}>
                <university.GridViewSwiper
                  width={boxRef?.current?.clientWidth}
                  data={trending}
                />
              </div>
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">UniVision Top Picks for You</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topPicks?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topPicks}
                shownumber={true}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />

          <div className="header-sm pl-3">
            Popular Universities in Your Country
          </div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : popularInMyCountry?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={popularInMyCountry}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">Top Business Schools</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topBusiness?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topBusiness}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">Top Medicine Schools</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topMedicine?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topMedicine}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">Top Law Schools</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topLaw?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topLaw}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">
            Top Engineering and Technology Schools
          </div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topEngineering?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topEngineering}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">Top Arts and Humanities Schools</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topArtsAndHumanities?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topArtsAndHumanities}
              />
            )}
          </boxContainer.LiteBoxContainer>
          <Spacer height={10} />
          <div className="header-sm pl-3">Top Psychology Schools</div>
          <Spacer height={10} />
          <boxContainer.LiteBoxContainer>
            {isGridLoading ? (
              <Loader.Circular />
            ) : topPsychology?.length === 0 ? (
              <Empty />
            ) : (
              <university.GridViewSwiper
                width={boxRef?.current?.clientWidth}
                data={topPsychology}
              />
            )}
          </boxContainer.LiteBoxContainer>
          {youMayLike?.length > 0 && (
            <>
              <Spacer height={10} />
              <div className="header-sm pl-3">Universities You May Like</div>
              <Spacer height={10} />
              <boxContainer.LiteBoxContainer>
                {isGridLoading ? (
                  <Loader.Circular />
                ) : youMayLike?.length === 0 ? (
                  <Empty />
                ) : (
                  <university.GridViewSwiper
                    width={boxRef?.current?.clientWidth}
                    data={youMayLike}
                  />
                )}
              </boxContainer.LiteBoxContainer>
            </>
          )}
        </div>
        <Spacer height={30} />
      </boxContainer.UniversityBoxContainer>
    </div>
  );
};

export default UniversityLayout;
